import { OrderStatus, QuotationStatus } from "../../util/Constant";
import { OrderDetailChangeEvent, OrderDetailType } from "./OrderDetailHeader";

type Props = {
  readOnly: boolean;
  type: OrderDetailType;
  status: string;
  discountRate: number | undefined;
  reQuotationFlg: boolean; // 再見積フラグ
  /** 値変更イベント */
  onChange: (e: OrderDetailChangeEvent) => void;
  /** 割引額計算ボタンクリックイベント */
  onCalculateDiscount?: () => void;
};

function OrderDetailDiscountRate(props: Props) {
  const discountRate = props.discountRate;

  // 割引率の入力値検証（整数部最大3桁、小数部最大2桁）
  function validateAndFormatDiscountRate(value: string): string {
    // 空文字は許可
    if (value === "") return value;

    // 数値に変換
    const numValue = parseFloat(value);

    // NaNまたは負の値は無効
    if (isNaN(numValue) || numValue < 0) return "";

    // 999.99を超える場合は999.99に制限
    if (numValue > 999.99) return "999.99";

    // 小数点で分割して桁数チェック
    const parts = value.split(".");

    // 整数部が3桁を超える場合
    if (parts[0] && parts[0].length > 3) {
      const integerPart = parts[0].substring(0, 3);
      const decimalPart = parts[1] ? parts[1].substring(0, 2) : "";
      return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
    }

    // 小数部が2桁を超える場合
    if (parts[1] && parts[1].length > 2) {
      return `${parts[0]}.${parts[1].substring(0, 2)}`;
    }

    return value;
  }

  // 無効なキーの入力を防止（小数点は許可）
  function onKeyDown(e: any) {
    // マイナス、プラス、eキーのみ無効化（小数点は許可）
    if (e.key === "-" || e.key === "+" || e.key === "e" || e.key === "E") {
      e.preventDefault();
      return;
    }
  }

  // テキストボックス変更
  function handleChangeText(e: React.BaseSyntheticEvent<any>) {
    const name = e.target.name;
    let value = e.target.value;

    // 入力値の検証とフォーマット
    const formattedValue = validateAndFormatDiscountRate(value);

    // フォーマット後の値が元の値と異なる場合、input要素の値を更新
    if (formattedValue !== value) {
      e.target.value = formattedValue;
      value = formattedValue;
    }

    // 元画面に連携
    props.onChange({ name, value });
  }

  // 割引額計算ボタンクリック
  function handleCalculateClick() {
    if (props.onCalculateDiscount) {
      props.onCalculateDiscount();
    }
  }

  // テキスト入力可否判定
  function enableText() {
    if (props.readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }
    if (props.type === OrderDetailType.Quotation) {
      // 見積の場合
      // 「作成中」「依頼中」の場合
      return [QuotationStatus.DRAFT, QuotationStatus.REQUESTED].includes(
        props.status
      );
    } else {
      // 注文の場合
      // 「注文中」、「部品手配中」、「完了」の場合
      return (
        props.status === OrderStatus.ORDERED ||
        props.status === OrderStatus.ARRANGING ||
        props.status === OrderStatus.COMPLETED
      );
    }
  }

  function discountRateAreaRenderer() {
    return (
      <>
        <>
          <div className="input-group">
            <span className="b-input-group-text w-40">
              {"割引率/Discount Rate"}
            </span>
            <input
              type="number"
              name="discountRate"
              className="form-control"
              style={{
                textAlign: "right",
              }}
              value={discountRate ?? ""}
              disabled={!enableText()}
              onKeyDown={onKeyDown}
              onChange={handleChangeText}
              min="0"
              max="999.99"
              step="0.01"
              placeholder="0.00"
              data-cy="割引率入力欄"
            />
            <span className="input-group-text">%</span>
          </div>
        </>
        {enableText() && (
          <div className="text-center">
            <button
              type="button"
              className="btn b-btn-primary w-40"
              onClick={handleCalculateClick}
              data-cy="割引額計算ボタン"
              style={{
                marginTop: "5px",
                marginBottom: "0px",
              }}
            >
              {"割引額に反映"}
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <div
      className="p-1 bg-white b-radius"
      style={{
        padding: "8px 8px 5px 8px",
      }}
    >
      {discountRateAreaRenderer()}
    </div>
  );
}

export default OrderDetailDiscountRate;
